"use strict";
/**
 * Payroll Features by Employment Type (GH#204)
 *
 * Defines which payroll calculation features each employment type supports
 * and how legal concepts are renamed for hourly-based types.
 *
 * Used by:
 * - Backend: payroll calculation engine (which items to include)
 * - Frontend: payroll estimate UI (which fields to show/hide)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAYROLL_CONCEPT_LABELS = exports.PAYROLL_FEATURES = void 0;
exports.isHourlyBasedType = isHourlyBasedType;
exports.isSalaryBasedType = isSalaryBasedType;
exports.getPayrollConceptLabel = getPayrollConceptLabel;
const employment_enums_1 = require("./employment.enums");
// ─── Employment Type Classification Helpers ──────────────────────────────────
/**
 * Hourly-based types use non-legal denomination for certain concepts
 * to avoid labor law implications (prima → apoyo, aguinaldo → apoyo de fin de año).
 */
function isHourlyBasedType(type) {
    return (type === employment_enums_1.EmploymentType.hourly_agreed ||
        type === employment_enums_1.EmploymentType.casual ||
        type === employment_enums_1.EmploymentType.intern);
}
/**
 * Salary-based types use legal denomination (prima dominical, aguinaldo, etc.)
 * and are eligible for full tax calculation (ISR).
 */
function isSalaryBasedType(type) {
    return (type === employment_enums_1.EmploymentType.payroll ||
        type === employment_enums_1.EmploymentType.payroll_draft);
}
exports.PAYROLL_FEATURES = {
    [employment_enums_1.EmploymentType.payroll]: {
        usesHourlyBasePay: false,
        hasAttendanceDiscounts: true,
        hasPunctualityBonus: true,
        hasOvertime: true,
        hasGuaranteedIncome: true,
        hasIsr: true,
        hasAguinaldo: true,
        hasPtu: true,
        usesRenamedConcepts: false,
        generatesRealPayroll: true,
    },
    [employment_enums_1.EmploymentType.payroll_draft]: {
        usesHourlyBasePay: false,
        hasAttendanceDiscounts: true,
        hasPunctualityBonus: true,
        hasOvertime: true,
        hasGuaranteedIncome: true,
        hasIsr: false,
        hasAguinaldo: true,
        hasPtu: true,
        usesRenamedConcepts: false,
        generatesRealPayroll: true,
    },
    [employment_enums_1.EmploymentType.hourly_agreed]: {
        usesHourlyBasePay: true,
        hasAttendanceDiscounts: true,
        hasPunctualityBonus: false,
        hasOvertime: false,
        hasGuaranteedIncome: false,
        hasIsr: false,
        hasAguinaldo: true,
        hasPtu: true,
        usesRenamedConcepts: true,
        generatesRealPayroll: true,
    },
    [employment_enums_1.EmploymentType.casual]: {
        usesHourlyBasePay: true,
        hasAttendanceDiscounts: false,
        hasPunctualityBonus: false,
        hasOvertime: false,
        hasGuaranteedIncome: false,
        hasIsr: false,
        hasAguinaldo: true,
        hasPtu: true,
        usesRenamedConcepts: true,
        generatesRealPayroll: true,
    },
    [employment_enums_1.EmploymentType.intern]: {
        usesHourlyBasePay: true,
        hasAttendanceDiscounts: false,
        hasPunctualityBonus: false,
        hasOvertime: false,
        hasGuaranteedIncome: false,
        hasIsr: false,
        hasAguinaldo: true,
        hasPtu: true,
        usesRenamedConcepts: true,
        generatesRealPayroll: true,
    },
    [employment_enums_1.EmploymentType.coverage_only]: {
        usesHourlyBasePay: false,
        hasAttendanceDiscounts: false,
        hasPunctualityBonus: false,
        hasOvertime: false,
        hasGuaranteedIncome: false,
        hasIsr: false,
        hasAguinaldo: false,
        hasPtu: false,
        usesRenamedConcepts: false,
        generatesRealPayroll: false,
    },
    [employment_enums_1.EmploymentType.contractor]: {
        usesHourlyBasePay: false,
        hasAttendanceDiscounts: false,
        hasPunctualityBonus: false,
        hasOvertime: false,
        hasGuaranteedIncome: false,
        hasIsr: false,
        hasAguinaldo: false,
        hasPtu: false,
        usesRenamedConcepts: false,
        generatesRealPayroll: false,
    },
};
// ─── Payroll Concept Denomination ───────────────────────────────────────────
/**
 * Payroll concept keys that have different labels depending on whether
 * the employment type is salary-based (legal terms) or hourly-based (non-legal).
 *
 * Hourly-based types use "apoyo" denomination to avoid labor law implications.
 */
exports.PAYROLL_CONCEPT_LABELS = {
    sundayBonus: {
        salary: "Prima dominical",
        hourly: "Apoyo dominical",
    },
    holidayOrRestExtraPay: {
        salary: "Pago extra por día festivo",
        hourly: "Apoyo por día festivo",
    },
    vacationBonus: {
        salary: "Prima vacacional",
        hourly: "Apoyo vacacional",
    },
    endYearBonus: {
        salary: "Aguinaldo",
        hourly: "Apoyo de fin de año",
    },
};
/**
 * Get the display label for a payroll concept based on employment type.
 *
 * @param conceptKey - Key from PAYROLL_CONCEPT_LABELS (e.g., "sundayBonus")
 * @param employmentType - The employment type to determine denomination
 * @returns The correct Spanish label for this concept
 */
function getPayrollConceptLabel(conceptKey, employmentType) {
    const entry = exports.PAYROLL_CONCEPT_LABELS[conceptKey];
    if (!entry)
        return conceptKey;
    return isHourlyBasedType(employmentType) ? entry.hourly : entry.salary;
}
